import { fmtNum } from '../ui/ListFoot';
import { booksChanged } from '../lib/changed';
import { supabase } from '../lib/supabase';
import type { NoticeInput } from '../shell/notice';

// Library copies' own changes (Part 2), each as you, each with Undo in the shared notice. The
// library steps themselves are the book page's (LoanSteps), loaded with the book.

type Notice = (n: NoticeInput) => void;

async function call<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  if (!supabase) throw new Error('Not connected to a database.');
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw new Error(error.message);
  return data as T;
}

async function undo(notice: Notice, action: string, title: string) {
  try {
    const res = await call<{ kept?: string[] } | null>('undo_action', { action });
    booksChanged();
    notice({ message: res?.kept?.length ? 'Undone, except what changed since' : `Undone: ${title} is as it was` });
  } catch (e) {
    notice({ message: `Couldn’t undo: ${e instanceof Error ? e.message : String(e)}`, done: false });
  }
}

/** Save the hold (Kristi, Oct 31): Add a copy's Library loan, on hold, with Libby's estimate. */
export async function placeHold(
  notice: Notice,
  book: { id: string; title: string },
  hold: { format: string; listen: boolean; expectedOn: string | null; libraryId: string | null },
): Promise<boolean> {
  try {
    const details = {
      how: 'library_loan',
      format: hold.format,
      listen: hold.listen,
      loan: { step: 'on_hold', expected_on: hold.expectedOn, ...(hold.libraryId ? { library_id: hold.libraryId } : {}) },
    };
    const action = await call<string>('copy_add', { book: book.id, details });
    booksChanged();
    notice({ message: `Hold saved: ${book.title}`, undo: () => void undo(notice, action, book.title) });
    return true;
  } catch (e) {
    notice({ message: `Hold not saved: ${e instanceof Error ? e.message : String(e)}`, done: false });
    return false;
  }
}

/** Not from the library, or Suggest again (0045). */
export async function setNotFromLibrary(notice: Notice, book: { id: string; title: string }, on: boolean): Promise<boolean> {
  try {
    const action = await call<string | null>('not_from_library', { book: book.id, turn_on: on });
    booksChanged();
    if (action) notice({ message: on ? `${book.title}: not from the library` : `${book.title}: suggested for a hold again`, undo: () => void undo(notice, action, book.title) });
    return true;
  } catch (e) {
    notice({ message: `Not saved: ${e instanceof Error ? e.message : String(e)}`, done: false });
    return false;
  }
}

// ---------- Owned copies (Part 3, 0046) ----------

async function undoGroup(notice: Notice, group: string) {
  try {
    const res = await call<{ undone: number; kept?: string[] }>('undo_group', { grp: group });
    booksChanged();
    notice({ message: `Undone for ${res.undone === 1 ? '1 copy' : `${fmtNum(res.undone)} copies`}${res.kept?.length ? ', except what changed since' : ''}` });
  } catch (e) {
    notice({ message: `Couldn’t undo: ${e instanceof Error ? e.message : String(e)}`, done: false });
  }
}

/** Where one copy or several are (a room by name, made on the spot; your words for the shelf). */
export async function placeCopies(notice: Notice, copies: string[], room: string | null, shelf: string | null, words: string): Promise<boolean> {
  try {
    const res = await call<{ group: string | null; changed: number; skipped: { reason: string }[] }>('copy_place', { copies, room: room ?? '', shelf: shelf ?? '' });
    booksChanged();
    if (!res.changed) {
      notice({ message: res.skipped.length ? `Not moved: ${res.skipped[0]?.reason ?? ''}` : 'Already there', done: !res.skipped.length });
      return res.skipped.length === 0;
    }
    const n = res.changed === 1 ? words : `${fmtNum(res.changed)} copies`;
    const where = room ? `in ${room}${shelf ? ` · ${shelf}` : ''}` : 'not placed';
    notice({
      message: `${n} now ${where}${res.skipped.length ? ` · ${res.skipped.length} left as they were` : ''}`,
      undo: res.group ? () => void undoGroup(notice, res.group!) : undefined,
    });
    return true;
  } catch (e) {
    notice({ message: `Not moved: ${e instanceof Error ? e.message : String(e)}`, done: false });
    return false;
  }
}

/**
 * A Kindle or Audible ID pasted on Owned copies' dashed "No Kindle ID ▾" (filters review, Kristi
 * Nov 2): saved on the copy's edition through copy_edit, as Update details' lookup list does, with
 * Undo. Returns an error to show beside the box, or null once saved.
 */
export async function saveCopyId(notice: Notice, copy: { id: string; title: string }, field: 'asin' | 'audible_id', id: string): Promise<string | null> {
  try {
    const action = await call<string | null>('copy_edit', { copy: copy.id, field, value: id });
    booksChanged();
    const what = field === 'asin' ? 'Kindle ID' : 'Audible ID';
    notice({ message: `${copy.title}: ${what} ${id} saved`, undo: action ? () => void undo(notice, action, copy.title) : undefined });
    return null;
  } catch (e) {
    return `Not saved: ${e instanceof Error ? e.message : String(e)}`;
  }
}

/** Lend it…: who has it (your words, never shared) and since when. */
export async function lendCopy(notice: Notice, copy: { id: string; title: string }, to: string, on: string | null): Promise<boolean> {
  try {
    const action = await call<string>('copy_lend', { copy: copy.id, lent_to: to, lent_on: on });
    booksChanged();
    notice({ message: `${copy.title} lent to ${to.trim()}`, undo: () => void undo(notice, action, copy.title) });
    return true;
  } catch (e) {
    notice({ message: `Not saved: ${e instanceof Error ? e.message : String(e)}`, done: false });
    return false;
  }
}

/** Got it back: the copy is yours again. */
export async function gotBack(notice: Notice, copy: { id: string; title: string }): Promise<boolean> {
  try {
    const action = await call<string>('copy_got_back', { copy: copy.id });
    booksChanged();
    notice({ message: `${copy.title}: got it back`, undo: () => void undo(notice, action, copy.title) });
    return true;
  } catch (e) {
    notice({ message: `Not saved: ${e instanceof Error ? e.message : String(e)}`, done: false });
    return false;
  }
}

/** I still have it: a lost or donated copy is yours again. */
export const stillHave = (notice: Notice, copy: { id: string; title: string }) => setCopiesState(notice, [copy], 'have_it');

/**
 * Mark lost · Mark donated · I still have it, for one copy or several (copy_edit's state, since
 * 0030). Several copies are one notice whose Undo takes each back.
 */
export async function setCopiesState(notice: Notice, copies: { id: string; title: string }[], state: 'lost' | 'donated' | 'have_it'): Promise<boolean> {
  const done: string[] = [];
  const failed: string[] = [];
  for (const c of copies) {
    try {
      done.push(await call<string>('copy_edit', { copy: c.id, field: 'state', value: state }));
    } catch (e) {
      failed.push(`${c.title}: ${e instanceof Error ? e.message : String(e)}`);
    }
  }
  if (done.length) booksChanged();
  const word = state === 'lost' ? 'marked lost' : state === 'donated' ? 'marked donated' : 'yours again';
  const who = copies.length === 1 ? copies[0]!.title : `${fmtNum(done.length)} copies`;
  if (!done.length) {
    notice({ message: `Not saved: ${failed[0] ?? ''}`, done: false });
    return false;
  }
  notice({
    message: copies.length === 1 && state === 'have_it' ? `${who}: you still have it` : `${who} ${word}${failed.length ? ` · ${failed.length} not changed` : ''}`,
    undo: async () => {
      let kept = false;
      for (const a of [...done].reverse()) {
        try {
          const res = await call<{ kept?: string[] } | null>('undo_action', { action: a });
          if (res?.kept?.length) kept = true;
        } catch {
          kept = true;
        }
      }
      booksChanged();
      notice({ message: kept ? 'Undone, except what changed since' : copies.length === 1 ? `Undone: ${who} is as it was` : `Undone for ${who}` });
    },
  });
  return true;
}

/**
 * Mark lost · Mark donated for books rather than copies (check-up, Kristi Nov 2): Recently added
 * marks an imported paper book you no longer have. Each book's one paper copy you still have is
 * marked; a book with two paper copies is left for Owned copies, where you pick the copy.
 */
export async function setBooksPaperState(notice: Notice, books: { id: string; title: string }[], state: 'lost' | 'donated'): Promise<boolean> {
  if (!supabase) return false;
  const { data, error } = await supabase
    .from('copies')
    .select('id, book_id')
    .in('book_id', books.map((b) => b.id))
    .eq('format', 'paper')
    .eq('state', 'have_it')
    .neq('how', 'library_loan');
  if (error) {
    notice({ message: `Not saved: ${error.message}`, done: false });
    return false;
  }
  const byBook = new Map<string, string[]>();
  for (const c of (data ?? []) as { id: string; book_id: string }[]) byBook.set(c.book_id, [...(byBook.get(c.book_id) ?? []), c.id]);
  const copies = books.filter((b) => byBook.get(b.id)?.length === 1).map((b) => ({ id: byBook.get(b.id)![0]!, title: b.title }));
  const two = books.filter((b) => (byBook.get(b.id)?.length ?? 0) > 1);
  if (!copies.length) {
    notice({ message: two.length ? `${two[0]!.title} has more than one paper copy: mark the right one on Owned copies` : 'No paper copy to mark', done: false });
    return false;
  }
  return setCopiesState(notice, copies, state);
}
